import jsPDF from "jspdf";
import html2canvas from "html2canvas";
import moment from "moment";

const MIN_SLICE_PX = 12;
const JPEG_QUALITY = 0.94;
const A4_WIDTH_MM = 210;
const A4_HEIGHT_MM = 297;

function chunkCanvasToPdf(pdf, canvas, pageWidth, pageHeight, ensurePage) {
  const sliceHeightPx = Math.floor((pageHeight * canvas.width) / pageWidth);
  let offsetY = 0;
  let sliceIndex = 0;

  while (offsetY < canvas.height) {
    const remaining = canvas.height - offsetY;
    if (remaining < MIN_SLICE_PX && sliceIndex > 0) {
      break;
    }

    const sliceH = Math.min(sliceHeightPx, remaining);
    const sliceCanvas = document.createElement("canvas");
    sliceCanvas.width = canvas.width;
    sliceCanvas.height = sliceH;
    const ctx = sliceCanvas.getContext("2d");
    ctx.fillStyle = "#ffffff";
    ctx.fillRect(0, 0, sliceCanvas.width, sliceCanvas.height);
    ctx.drawImage(canvas, 0, offsetY, canvas.width, sliceH, 0, 0, canvas.width, sliceH);

    const sliceHmm = (sliceH * pageWidth) / canvas.width;
    ensurePage();
    pdf.addImage(
      sliceCanvas.toDataURL("image/jpeg", JPEG_QUALITY),
      "JPEG",
      0,
      0,
      pageWidth,
      sliceHmm,
      undefined,
      "FAST"
    );

    offsetY += sliceHeightPx;
    sliceIndex += 1;
  }
}

/**
 * One .mis-print-slice ≈ one PDF sheet. Variable page height when content is shorter than A4
 * (avoids huge white gaps). Sublice only if a slice is still taller than A4.
 */
async function addPrintSliceToPdf(pdf, el, pageWidth, pageHeight, ensurePage) {
  const canvas = await html2canvas(el, {
    scale: 2,
    useCORS: true,
    backgroundColor: "#ffffff",
    logging: false,
    height: el.scrollHeight,
    windowHeight: el.scrollHeight,
    scrollX: 0,
    scrollY: -window.scrollY,
    imageTimeout: 15000,
  });

  const pxPerMm = canvas.width / pageWidth;
  const maxHeightPx = Math.floor(pageHeight * pxPerMm);

  if (canvas.height <= maxHeightPx) {
    const imgHmm = canvas.height / pxPerMm;
    ensurePage();
    pdf.internal.pageSize.setWidth(pageWidth);
    pdf.internal.pageSize.setHeight(imgHmm);
    pdf.addImage(
      canvas.toDataURL("image/jpeg", JPEG_QUALITY),
      "JPEG",
      0,
      0,
      pageWidth,
      imgHmm,
      undefined,
      "FAST"
    );
    return;
  }

  chunkCanvasToPdf(pdf, canvas, pageWidth, pageHeight, ensurePage);
}

export async function exportMisReportPdf(printRoot, { branch, date }) {
  if (!printRoot) {
    throw new Error("Print document not ready");
  }

  const slices = printRoot.querySelectorAll(".mis-print-slice");
  const pages = slices.length
    ? [...slices]
    : [...printRoot.querySelectorAll(".mis-print-page")];

  if (!pages.length) {
    throw new Error("No print pages found");
  }

  const pdf = new jsPDF({
    orientation: "p",
    unit: "mm",
    format: "a4",
    compress: true,
  });
  const pageWidth = pdf.internal.pageSize.getWidth();
  const pageHeight = pdf.internal.pageSize.getHeight();
  let pdfPageOpen = false;

  const ensurePage = () => {
    if (pdfPageOpen) {
      pdf.addPage("a4", "p");
    }
    pdfPageOpen = true;
    pdf.internal.pageSize.setWidth(A4_WIDTH_MM);
    pdf.internal.pageSize.setHeight(A4_HEIGHT_MM);
  };

  for (let i = 0; i < pages.length; i++) {
    await addPrintSliceToPdf(pdf, pages[i], pageWidth, pageHeight, ensurePage);
  }

  const label =
    branch === "1" ? "G13" : branch === "15" ? "F11" : "AllBranches";
  pdf.save(`MIS_Report_${label}_${moment(date).format("YYYY-MM-DD")}.pdf`);
}
